import * as R from 'ramda';
import { useState } from 'react';
import { OuterClick } from 'react-outer-click';
// mui
import Typography from '@mui/material/Typography';
// icons-material
import AddIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
// constants
import { FIELD_DATE } from '../../../../constants';
// components
import { newReportConditions } from '../../../report-format-panel/settings/filter-settings';
// utilities
import * as U from '../../../../utilities';
// ui
import { Box, Flex, Divider } from '../../../../ui';
// component edit-report
import { withReportFilterList } from './hocs';
import { renderField, FilterSelect } from './fields';
//////////////////////////////////////////////////

const setDataRelative = filter => {
  const operation = U.getPropFromObject('operation', filter);
  const dateRelative = U.getPropFromObject('dateRelative', filter);

  if (R.equals(operation, 'time_unit')) return true;

  return dateRelative;
};

const FilterHeader = ({ theme }) => (
  <Flex
    p='0 15px'
    height={40}
    borderBottom='1px solid'
    bg={theme.colors.bgColor}
    justifyContent='space-between'
    color={theme.colors.lightBlack}
    borderColor={theme.colors.middleGrey}
  >
    <Typography>{U.getTranslation('labels.filter', 'Filter')}</Typography>
  </Flex>
);

const FilterItemAction = props => {
  const {
    theme,
    groupId,
    filterIndex,
    handleRemoveReportFilter,
  } = props;

  return (
    <Flex>
      <Box>
        <Flex
          ml={10}
          cursor='pointer'
          alignItems='center'
          title={U.getTranslation('actions.remove', 'Remove')}
          onClick={() => handleRemoveReportFilter(groupId, filterIndex)}
        >
          <DeleteIcon size='25px' sx={{ color: theme.colors.success }} />
        </Flex>
      </Box>
    </Flex>
  );
};

const FilterItem = props => {
  const {
    theme,
    zIndex,
    groupId,
    dataType,
    timeUnit,
    operation,
    collection,
    filterIndex,
    groupLength,
    dateRelative,
    propertyName,
    withoutOrGroup,
    availableFields,
    filterFieldWidth,
    handleSelectOperation,
    handleSelectPropertyName,
  } = props;

  const [changedZIndex, setChangedZIndex] = useState(props => R.pathOr(10, ['zIndex'], props)); // eslint-disable-line

  const handleChangeZIndex = () => setChangedZIndex(1000);
  const handleReturnZIndex = () => setChangedZIndex(R.or(zIndex, 10));

  const operationToUse = U.ifElse(
    R.and(R.equals(dataType, FIELD_DATE), U.isTrue(dateRelative)),
    timeUnit,
    operation,
  );
  const zIndexToUse = R.or(changedZIndex, zIndex);
  const showDivider = U.notEquals(groupLength, R.inc(filterIndex));
  const dividerText = U.ifElse(
    U.isTrue(withoutOrGroup),
    U.getTranslation('labels.and', 'and'),
    U.getTranslation('labels.or', 'or'),
  );

  return (
    <OuterClick as={Box} width='100%' zIndex={zIndexToUse} onOuterClick={handleReturnZIndex}>
      <>
        <Flex mb={10} alignItems='center' onClick={handleChangeZIndex}>
          <FilterItemAction {...props} />
          <FilterSelect
            theme={theme}
            zI={zIndexToUse}
            groupId={groupId}
            withoutOrGroup={true}
            collection={collection}
            filterIndex={filterIndex}
            options={availableFields}
            id={`filter-field-${filterIndex}`}
            action={handleSelectPropertyName}
            filterFieldWidth={filterFieldWidth}
            value={propertyName ? U.getTranslation(`labels.${propertyName}`, propertyName) : ''}
          />
          <FilterSelect
            theme={theme}
            groupId={groupId}
            dataType={dataType}
            filterIndex={filterIndex}
            filterFieldWidth={filterFieldWidth}
            id={`filter-operation-${filterIndex}`}
            options={newReportConditions()[R.or(dataType, 'default')]}
            action={values => handleSelectOperation(values, filterIndex, dataType)}
            value={operationToUse ? U.getTranslation(`labels.${operationToUse}`, operationToUse) : ''}
          />
          {
            U.isNotNilAndNotEmpty(operationToUse)
            && renderField({ ...props, dataType, id: `filter-value-${filterIndex}` })
          }
        </Flex>
        { showDivider && <Divider theme={theme} text={dividerText} /> }
      </>
    </OuterClick>
  );
};

const FilterGroupTitle = props => {
  const {
    theme,
    groupId,
    groupLength,
    handleAddReportFilter,
    reportSimpleFiltersGroupId,
  } = props;

  const showAddFilter = R.lt(groupLength, 20);
  const withoutOrGroup = R.equals(groupId, reportSimpleFiltersGroupId);
  const title = U.getTranslation('labels.exactMatchingFilters', 'Exact matching filters(and)');

  return (
    <Flex my={10} width='100%' justifyContent='center'>
      <Box
        fontSize={14}
        cursor='pointer'
        fontWeight={500}
        color={theme.colors.success}
      >
        {title}
      </Box>
      {
        showAddFilter
        && (
        <Box>
          <Flex
            ml={10}
            display='flex'
            cursor='pointer'
            alignItems='center'
            hoveredOpacity={0.5}
            title={U.getTranslation('actions.addFilter', 'Filter')}
            onClick={() => handleAddReportFilter({ groupId, withoutOrGroup })}
          >
            <AddIcon sx={{ fontSize: '20px', color: theme.colors.success }} />
          </Flex>
        </Box>
        )
      }
    </Flex>
  );
};

const FilterList = props => {
  const {
    theme,
    filterBy,
    useExactFilters,
    availableFields,
    filterFieldWidth,
    hideFilterHeader,
    handleChangeDate,
    handleChangeInput,
    handleChangeRange,
    reportFilterGroups,
    handleChangeSelect,
    reportFilterParams,
    filterFieldRangeWidth,
    handleSelectOperation,
    handleAddReportFilter,
    handleChangeMultiSelect,
    handleSelectPropertyName,
    handleRemoveReportFilter,
    reportSimpleFiltersGroupId,
  } = props;

  const groupKeys = R.keys(reportFilterGroups);
  const showExactFiltersTitle = R.compose(
    R.not,
    R.any(R.propEq(true, 'withoutOrGroup')),
    R.reduce(R.concat, []),
    R.values,
  )(reportFilterGroups);

  return (
    <Box width={U.ifElse(U.isTrue(useExactFilters), 620, 780)}>
      {R.not(hideFilterHeader) && <FilterHeader {...props} />}
      <Box>
        {
          U.isTrue(showExactFiltersTitle)
          && (
          <FilterGroupTitle
            theme={theme}
            groupLength={0}
            groupId={reportSimpleFiltersGroupId}
            handleAddReportFilter={handleAddReportFilter}
            reportSimpleFiltersGroupId={reportSimpleFiltersGroupId}
          />
          )
        }
        {
          U.isNilOrEmpty(groupKeys)
          && (
          <Box p={15}>
            <Flex flexDirection='column'>
              <Typography fontSize={15} color={theme.colors.darkGrey}>
                {U.getTranslation(
                  'errors.youDontHaveAnyFiltersForThisReport',
                  'You don\'t have any filters for this Report',
                )}
              </Typography>
            </Flex>
          </Box>
          )
        }
        {
          groupKeys.map((groupId, groupIndex) => (
            <Flex
              py={10}
              key={groupId}
              alignItems='start'
              border='1px solid'
              flexDirection='column'
              borderColor={theme.colors.lightGrey}
            >
              <FilterGroupTitle
                theme={theme}
                groupId={groupId}
                handleAddReportFilter={handleAddReportFilter}
                groupLength={R.length(reportFilterGroups[groupId])}
                reportSimpleFiltersGroupId={reportSimpleFiltersGroupId}
              />
              {
                reportFilterGroups[groupId].map((filter, index) => (
                  <FilterItem
                    {...filter}
                    key={index}
                    theme={theme}
                    filterBy={filterBy}
                    filterIndex={index}
                    groupIndex={groupIndex}
                    availableFields={availableFields}
                    filterFieldWidth={filterFieldWidth}
                    handleChangeDate={handleChangeDate}
                    handleChangeRange={handleChangeRange}
                    handleChangeInput={handleChangeInput}
                    dateRelative={setDataRelative(filter)}
                    handleChangeSelect={handleChangeSelect}
                    reportFilterParams={reportFilterParams}
                    filterFieldRangeWidth={filterFieldRangeWidth}
                    handleSelectOperation={handleSelectOperation}
                    handleChangeMultiSelect={handleChangeMultiSelect}
                    groupLength={R.length(reportFilterGroups[groupId])}
                    handleRemoveReportFilter={handleRemoveReportFilter}
                    handleSelectPropertyName={handleSelectPropertyName}
                    zIndex={U.curriedParseInt(R.subtract(R.divide(150, R.inc(groupIndex)), R.inc(index)))}
                  />
                ))
              }
            </Flex>
          ))
        }
      </Box>
    </Box>
  );
};

export default withReportFilterList(FilterList);
